///
/// EarthBrowser
///
/// Created by Matt Giger on 10/27/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Shapes for a layer's "why is the picture incomplete" state. A layer that
/// is on the globe but still fetching or processing publishes one of these
/// so the UI can tell the user data is coming (animated indicator) rather
/// than letting an empty region read as "no data" (static). Kept
/// dependency-free, like TimeLane, so both engine and React import it.
///

/**
 * What the layer is waiting on. Listed in display priority: when several
 * sources are active at once the earliest phase in this list is shown.
 *
 * - error: the last sync failed and will be retried
 * - downloading: shards / deltas in flight from the CDN
 * - ingesting: downloaded rows being written to SQLite, or a compute step
 * - building: scene objects being constructed in chunks (large sets)
 * - frames: a time-series layer whose frame for the clock's time is not
 *   loaded yet (a fallback frame or nothing is on screen)
 */
export type LoadPhase = "error" | "downloading" | "ingesting" | "building" | "frames"

export const cLoadPhaseOrder: LoadPhase[] = ["error", "downloading", "ingesting", "building", "frames"]

export interface LayerLoadState {
	phase: LoadPhase
	/** 0-1 when the work has a known extent, otherwise indeterminate */
	progress?: number
	/** Short human detail: "2.1 MB", "1,200 / 4,800" */
	detail?: string
	/** For `frames`: the frame time (ms epoch) the clock wants; the UI formats it */
	atMs?: number
	/** When this phase began (performance.now() ms), for UI hysteresis */
	since: number
}

/** One layer's load state as published to the UI */
export interface LayerLoadReport extends LayerLoadState {
	/** Engine layer name (DatasetLayer.name / external provider id) */
	id: string
	/** The layer is on the globe; hidden layers still sync but don't affect the picture */
	visible: boolean
}

/** Format a byte count the way the layer rows show shard sizes */
export function formatBytes(bytes: number): string {
	if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`
	if (bytes >= 1_000) return `${Math.round(bytes / 1_000)} KB`
	return `${bytes} B`
}

/** Pick the state to show when several sources are active at once */
export function pickLoadState(states: Array<LayerLoadState | null | undefined>): LayerLoadState | null {
	let best: LayerLoadState | null = null
	for (const state of states) {
		if (!state) continue
		if (!best || cLoadPhaseOrder.indexOf(state.phase) < cLoadPhaseOrder.indexOf(best.phase)) {
			best = state
		}
	}
	return best
}
